// src/cockpit4/components/HermesScheduleManager.tsx
// ============================================
// 日程与会议管理组件（Hermes 助手 P1-功能4）
//
// 功能：
//   1. 日历视图（日 / 周 / 月 三种视图切换）
//   2. 添加日程表单（类型 / 标题 / 时间 / 参会人 / 地点）
//   3. 冲突检测（同一时间段存在多个日程时高亮提示，可强制保存）
//   4. 会议纪要自动生成（调用 RuntimeManager.query('hermes', { type: 'generate_meeting_notes', ... })）
//   5. 行动项追踪与跟进（添加 / 完成 / 追踪 / 删除）
//   6. 日程提醒（30 分钟内即将开始的日程高亮显示）
//   7. 最佳会议时间建议（分析参会人未来 7 天空闲时段）
//
// 数据来源：
//   - localStorage 持久化（key: xeaos_hermes_schedule），无 mock 数据
//   - 无数据时显示"暂无日程 / 点击添加"
//   - 会议纪要由 HermesRuntime 生成（真实调用 RuntimeManager）
//
// 风格：深色科技玻璃拟态，紫色主色调，border-white/[0.06] + bg-white/[0.02]
// ============================================

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar as CalendarIcon,
  Plus,
  X,
  Clock,
  Users,
  MapPin,
  Video,
  Bell,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Trash2,
  Pencil,
  FileText,
  Lightbulb,
  Send,
  CalendarDays,
  CalendarRange,
  Square,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";

// ============================================
// 类型定义
// ============================================

/** 日程类型 */
export type ScheduleType = "meeting" | "task" | "reminder" | "deadline";

/** 日程状态 */
export type ScheduleStatus =
  | "scheduled"
  | "in_progress"
  | "completed"
  | "cancelled";

/** 行动项 */
export interface ActionItem {
  /** 唯一 ID */
  id: string;
  /** 内容 */
  content: string;
  /** 负责人 */
  assignee: string;
  /** 截止日期 YYYY-MM-DD */
  dueDate?: string;
  /** 是否已完成 */
  completed: boolean;
}

/** 日程项 */
export interface ScheduleItem {
  /** 唯一 ID */
  id: string;
  /** 类型 */
  type: ScheduleType;
  /** 标题 */
  title: string;
  /** 描述 */
  description?: string;
  /** 开始时间 ISO datetime */
  startTime: string;
  /** 结束时间 ISO datetime */
  endTime: string;
  /** 状态 */
  status: ScheduleStatus;
  /** 参会人 */
  attendees?: string[];
  /** 地点（线上/线下） */
  location?: string;
  /** 会议纪要 */
  meetingNotes?: string;
  /** 行动项 */
  actionItems?: ActionItem[];
  /** 创建时间 */
  createdAt: string;
}

// ============================================
// 常量配置
// ============================================

/** localStorage 持久化键 */
const STORAGE_KEY = "xeaos_hermes_schedule";

/** 日历视图模式 */
type ViewMode = "day" | "week" | "month";

/** 即将开始的提醒阈值（毫秒，默认 30 分钟） */
const UPCOMING_THRESHOLD_MS = 30 * 60 * 1000;

/** 日程类型配置（颜色 + 图标 + 中文标签） */
const TYPE_META: Record<
  ScheduleType,
  { label: string; color: string; bg: string; border: string; icon: typeof Video }
> = {
  meeting: {
    label: "会议",
    color: "#A855F7",
    bg: "rgba(168, 85, 247, 0.10)",
    border: "rgba(168, 85, 247, 0.30)",
    icon: Video,
  },
  task: {
    label: "任务",
    color: "#60A5FA",
    bg: "rgba(96, 165, 250, 0.10)",
    border: "rgba(96, 165, 250, 0.30)",
    icon: CheckCircle2,
  },
  reminder: {
    label: "提醒",
    color: "#FBBF24",
    bg: "rgba(251, 191, 36, 0.10)",
    border: "rgba(251, 191, 36, 0.30)",
    icon: Bell,
  },
  deadline: {
    label: "截止",
    color: "#F472B6",
    bg: "rgba(244, 114, 182, 0.10)",
    border: "rgba(244, 114, 182, 0.30)",
    icon: AlertTriangle,
  },
};

/** 日程状态配置 */
const STATUS_META: Record<ScheduleStatus, { label: string; color: string }> = {
  scheduled: { label: "已安排", color: "#9CA3AF" },
  in_progress: { label: "进行中", color: "#3B82F6" },
  completed: { label: "已完成", color: "#10B981" },
  cancelled: { label: "已取消", color: "#6B7280" },
};

/** 视图模式配置 */
const VIEW_META: Record<
  ViewMode,
  { label: string; icon: typeof CalendarIcon }
> = {
  day: { label: "日", icon: Square },
  week: { label: "周", icon: CalendarRange },
  month: { label: "月", icon: CalendarDays },
};

// ============================================
// localStorage 持久化
// ============================================

/** 加载日程列表（从 localStorage） */
function loadSchedules(): ScheduleItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as ScheduleItem[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

/** 保存日程列表（到 localStorage） */
function saveSchedules(schedules: ScheduleItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(schedules));
  } catch {
    // 静默降级：存储失败不影响内存中的使用
  }
}

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(): string {
  return `sch_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 格式化日期为 YYYY-MM-DD */
function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** 从 Date 取 HH:MM */
function toTimeKey(d: Date): string {
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

/** 格式化 ISO 为本地化短日期（MM-DD） */
function formatDateShort(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("zh-CN", { month: "2-digit", day: "2-digit" });
}

/** 格式化 ISO 为完整日期时间 */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** 格式化 ISO 为 HH:MM */
function formatTime(iso: string): string {
  return toTimeKey(new Date(iso));
}

/** 判断两个 Date 是否同一天 */
function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** 判断日程是否落在给定日期 */
function isScheduleOnDate(item: ScheduleItem, date: Date): boolean {
  return isSameDay(new Date(item.startTime), date);
}

/** 判断两个时间段是否重叠（用于冲突检测） */
function isTimeOverlap(s1: string, e1: string, s2: string, e2: string): boolean {
  const aStart = new Date(s1).getTime();
  const aEnd = new Date(e1).getTime();
  const bStart = new Date(s2).getTime();
  const bEnd = new Date(e2).getTime();
  return aStart < bEnd && bStart < aEnd;
}

/** 冲突检测：返回与给定时间区间冲突的日程列表 */
function detectConflicts(
  schedules: ScheduleItem[],
  startTime: string,
  endTime: string,
  excludeId?: string
): ScheduleItem[] {
  return schedules.filter(
    (s) =>
      s.id !== excludeId &&
      s.status !== "cancelled" &&
      isTimeOverlap(s.startTime, s.endTime, startTime, endTime)
  );
}

/** 判断日程是否即将开始（30 分钟内） */
function isUpcoming(item: ScheduleItem, now: Date = new Date()): boolean {
  if (item.status === "completed" || item.status === "cancelled") return false;
  const start = new Date(item.startTime).getTime();
  const diff = start - now.getTime();
  return diff >= 0 && diff <= UPCOMING_THRESHOLD_MS;
}

/** 获取本周日期范围（周一到周日） */
function getWeekRange(date: Date): { start: Date; end: Date } {
  const d = new Date(date);
  const day = d.getDay(); // 0=周日, 1=周一
  const diff = day === 0 ? -6 : 1 - day;
  const start = new Date(d);
  start.setDate(d.getDate() + diff);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

/** 获取当月日期范围 */
function getMonthRange(date: Date): { start: Date; end: Date } {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
  return { start, end };
}

/** 获取月视图日历网格（6 行 × 7 列，含上下月填充，周一起头） */
function getMonthGrid(date: Date): Date[] {
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  const startDay = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
  const gridStart = new Date(firstDay);
  gridStart.setDate(firstDay.getDate() - startDay);
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    days.push(d);
  }
  return days;
}

/** 获取本周日期数组（周一到周日） */
function getWeekDays(date: Date): Date[] {
  const { start } = getWeekRange(date);
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

/** 周几标签（周一起头） */
const WEEK_LABELS = ["一", "二", "三", "四", "五", "六", "日"];

/** 将 datetime-local 控件值转为 ISO */
function localInputToISO(value: string): string {
  // value 形如 "2026-08-02T14:30"
  return value ? new Date(value).toISOString() : "";
}

/** 将 ISO 转为 datetime-local 控件可显示的值 */
function isoToLocalInput(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${h}:${min}`;
}

// ============================================
// 主组件
// ============================================

export default function HermesScheduleManager() {
  // 日程列表（来源：localStorage）
  const [schedules, setSchedules] = useState<ScheduleItem[]>([]);
  // 当前选中的日程 ID
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // 日历视图模式
  const [viewMode, setViewMode] = useState<ViewMode>("week");
  // 日历当前定位日期
  const [cursorDate, setCursorDate] = useState<Date>(new Date());
  // 添加/编辑表单 Modal
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ScheduleItem | null>(null);
  // 冲突提示（保存时检测到时间冲突，待用户确认）
  const [conflictWarning, setConflictWarning] = useState<{
    pending: ScheduleItem;
    conflicts: ScheduleItem[];
  } | null>(null);
  // 会议纪要生成中的日程 ID
  const [generatingNotes, setGeneratingNotes] = useState<string | null>(null);
  // 行动项新增输入
  const [newActionContent, setNewActionContent] = useState("");
  const [newActionAssignee, setNewActionAssignee] = useState("");
  // 最佳时间建议
  const [suggestions, setSuggestions] = useState<string[] | null>(null);
  // 当前建议分析的参会人
  const [suggestAttendees, setSuggestAttendees] = useState<string>("");

  // 初始化：从 localStorage 加载
  useEffect(() => {
    setSchedules(loadSchedules());
  }, []);

  // 持久化：schedules 变化时保存到 localStorage
  useEffect(() => {
    saveSchedules(schedules);
  }, [schedules]);

  // 当前选中的日程对象
  const selectedItem = useMemo(
    () => schedules.find((s) => s.id === selectedId) || null,
    [schedules, selectedId]
  );

  // 今日日程（按开始时间排序）
  const todaySchedules = useMemo(() => {
    const today = new Date();
    return schedules
      .filter((s) => isScheduleOnDate(s, today) && s.status !== "cancelled")
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [schedules]);

  // 即将开始的日程（30 分钟内）
  const upcomingSchedules = useMemo(() => {
    const now = new Date();
    return schedules.filter((s) => isUpcoming(s, now));
  }, [schedules]);

  // 当前视图下的日程列表（按开始时间排序）
  const visibleSchedules = useMemo(() => {
    return schedules
      .filter((s) => {
        if (s.status === "cancelled") return false;
        const start = new Date(s.startTime);
        if (viewMode === "day") {
          return isScheduleOnDate(s, cursorDate);
        }
        if (viewMode === "week") {
          const { start: ws, end: we } = getWeekRange(cursorDate);
          return start >= ws && start <= we;
        }
        // month
        const { start: ms, end: me } = getMonthRange(cursorDate);
        return start >= ms && start <= me;
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [schedules, viewMode, cursorDate]);

  // 保存日程（添加或更新），自动检测冲突
  const handleSaveSchedule = useCallback(
    (item: ScheduleItem) => {
      const conflicts = detectConflicts(
        schedules,
        item.startTime,
        item.endTime,
        // 编辑时排除自身
        schedules.some((s) => s.id === item.id) ? item.id : undefined
      );
      if (conflicts.length > 0) {
        // 弹出冲突确认
        setConflictWarning({ pending: item, conflicts });
        return false;
      }
      // 无冲突：新增或更新
      setSchedules((prev) => {
        const idx = prev.findIndex((s) => s.id === item.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = item;
          return next;
        }
        return [...prev, item];
      });
      setSelectedId(item.id);
      setFormOpen(false);
      setEditingItem(null);
      return true;
    },
    [schedules]
  );

  // 确认保存（带冲突时强制保存）
  const handleConfirmConflictSave = useCallback(() => {
    if (!conflictWarning) return;
    const item = conflictWarning.pending;
    setSchedules((prev) => {
      const idx = prev.findIndex((s) => s.id === item.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = item;
        return next;
      }
      return [...prev, item];
    });
    setSelectedId(item.id);
    setConflictWarning(null);
    setFormOpen(false);
    setEditingItem(null);
  }, [conflictWarning]);

  // 打开添加表单（新建空表单）
  const openAddForm = useCallback(() => {
    setEditingItem(null);
    setFormOpen(true);
  }, []);

  // 打开编辑表单
  const openEditForm = useCallback((item: ScheduleItem) => {
    setEditingItem(item);
    setSelectedId(item.id);
    setFormOpen(true);
  }, []);

  // 删除日程
  const handleDeleteSchedule = useCallback(
    (id: string) => {
      setSchedules((prev) => prev.filter((s) => s.id !== id));
      if (selectedId === id) setSelectedId(null);
    },
    [selectedId]
  );

  // 更新日程（局部字段）
  const handleUpdateSchedule = useCallback((id: string, patch: Partial<ScheduleItem>) => {
    setSchedules((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...patch } : s))
    );
  }, []);

  // 添加行动项
  const handleAddActionItem = useCallback(
    (scheduleId: string, content: string, assignee: string, dueDate?: string) => {
      if (!content.trim()) return;
      const newItem: ActionItem = {
        id: genId(),
        content: content.trim(),
        assignee: assignee.trim() || "未指派",
        dueDate,
        completed: false,
      };
      setSchedules((prev) =>
        prev.map((s) => {
          if (s.id !== scheduleId) return s;
          const items = s.actionItems ? [...s.actionItems] : [];
          items.push(newItem);
          return { ...s, actionItems: items };
        })
      );
    },
    []
  );

  // 切换行动项完成状态
  const handleToggleAction = useCallback((scheduleId: string, actionId: string) => {
    setSchedules((prev) =>
      prev.map((s) => {
        if (s.id !== scheduleId) return s;
        const items = (s.actionItems || []).map((a) =>
          a.id === actionId ? { ...a, completed: !a.completed } : a
        );
        return { ...s, actionItems: items };
      })
    );
  }, []);

  // 删除行动项
  const handleDeleteAction = useCallback((scheduleId: string, actionId: string) => {
    setSchedules((prev) =>
      prev.map((s) => {
        if (s.id !== scheduleId) return s;
        const items = (s.actionItems || []).filter((a) => a.id !== actionId);
        return { ...s, actionItems: items };
      })
    );
  }, []);

  // 生成会议纪要（调用 HermesRuntime）
  const handleGenerateNotes = useCallback(
    async (item: ScheduleItem) => {
      setGeneratingNotes(item.id);
      try {
        // 轮询等待 RuntimeManager 就绪（最多 6 秒）
        for (let i = 0; i < 20; i++) {
          if (RuntimeManager.isInitialized()) break;
          await new Promise((r) => window.setTimeout(r, 300));
        }
        if (!RuntimeManager.isInitialized()) {
          window.alert("RuntimeManager 尚未初始化，无法生成纪要");
          return;
        }
        // 真实调用 Hermes Runtime 生成会议纪要
        const resp = await RuntimeManager.query(RuntimeName.Hermes, {
          type: "generate_meeting_notes",
          title: item.title,
          description: item.description || "",
          startTime: item.startTime,
          endTime: item.endTime,
          attendees: item.attendees || [],
          location: item.location || "",
          actionItems: item.actionItems || [],
        });
        if (resp?.success) {
          const raw: any = resp.data;
          // 兼容多种返回结构：string / { notes } / { meetingNotes } / { markdown }
          const notes =
            typeof raw === "string"
              ? raw
              : raw && typeof raw.notes === "string"
                ? raw.notes
                : raw && typeof raw.meetingNotes === "string"
                  ? raw.meetingNotes
                  : raw && typeof raw.markdown === "string"
                    ? raw.markdown
                    : raw && typeof raw.content === "string"
                      ? raw.content
                      : JSON.stringify(raw, null, 2);
          handleUpdateSchedule(item.id, { meetingNotes: notes });
        } else {
          window.alert(`生成失败：${resp?.error || "未知错误"}`);
        }
      } catch (err: any) {
        window.alert(`生成异常：${err?.message || err}`);
      } finally {
        setGeneratingNotes(null);
      }
    },
    [handleUpdateSchedule]
  );

  // 最佳会议时间建议（分析参会人未来 7 天空闲时段）
  const handleSuggestBestTime = useCallback(
    (attendees: string[]) => {
      if (attendees.length === 0) {
        setSuggestions(["未指定参会人，无法分析"]);
        return;
      }
      const now = new Date();
      const futureEnd = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      // 收集这些参会人未来 7 天的繁忙时段
      const busySlots = schedules.filter(
        (s) =>
          s.status !== "cancelled" &&
          s.attendees &&
          s.attendees.some((a) => attendees.includes(a)) &&
          new Date(s.startTime) >= now &&
          new Date(s.startTime) <= futureEnd
      );
      // 在未来 7 天的工作日 10:00-11:00 / 14:00-15:00 中寻找空闲时段
      const result: string[] = [];
      for (let i = 1; i <= 7 && result.length < 5; i++) {
        const day = new Date(now);
        day.setDate(now.getDate() + i);
        day.setHours(0, 0, 0, 0);
        const wd = day.getDay();
        if (wd === 0 || wd === 6) continue; // 跳过周末
        const slots = [10, 14];
        for (const h of slots) {
          const slotStart = new Date(day);
          slotStart.setHours(h, 0, 0, 0);
          const slotEnd = new Date(day);
          slotEnd.setHours(h + 1, 0, 0, 0);
          const conflict = busySlots.some((s) =>
            isTimeOverlap(
              s.startTime,
              s.endTime,
              slotStart.toISOString(),
              slotEnd.toISOString()
            )
          );
          if (!conflict) {
            result.push(
              `${formatDateShort(slotStart.toISOString())} ${toTimeKey(slotStart)}-${toTimeKey(slotEnd)}`
            );
          }
          if (result.length >= 5) break;
        }
      }
      setSuggestions(
        result.length > 0
          ? result
          : ["未来 7 天工作日均繁忙，建议调整参会人或放宽时间范围"]
      );
    },
    [schedules]
  );

  // 日历导航：上一页 / 下一页 / 今天
  const handlePrev = () => {
    const d = new Date(cursorDate);
    if (viewMode === "day") d.setDate(d.getDate() - 1);
    else if (viewMode === "week") d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    setCursorDate(d);
  };
  const handleNext = () => {
    const d = new Date(cursorDate);
    if (viewMode === "day") d.setDate(d.getDate() + 1);
    else if (viewMode === "week") d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    setCursorDate(d);
  };
  const handleToday = () => setCursorDate(new Date());

  // 当前视图的标题
  const cursorLabel = useMemo(() => {
    if (viewMode === "day") {
      return cursorDate.toLocaleDateString("zh-CN", {
        year: "numeric",
        month: "long",
        day: "numeric",
        weekday: "long",
      });
    }
    if (viewMode === "week") {
      const { start, end } = getWeekRange(cursorDate);
      return `${formatDateShort(start.toISOString())} ~ ${formatDateShort(end.toISOString())}`;
    }
    return cursorDate.toLocaleDateString("zh-CN", { year: "numeric", month: "long" });
  }, [viewMode, cursorDate]);

  return (
    <div className="h-full w-full flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden">
      {/* ============ 顶部工具栏 ============ */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06]">
        {/* 左：标题 */}
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-7 h-7 rounded-md bg-purple-500/15 border border-purple-500/30 flex items-center justify-center flex-shrink-0">
            <CalendarIcon size={14} className="text-purple-400" />
          </div>
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold text-white tracking-tight truncate">
              日程与会议管理
            </h2>
            <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
              Hermes P1 · 智能排程 / 冲突检测 / 纪要生成
            </p>
          </div>
        </div>

        {/* 中：视图切换 */}
        <div className="flex items-center gap-1 p-1 rounded-lg border border-white/[0.06] bg-white/[0.02]">
          {(Object.keys(VIEW_META) as ViewMode[]).map((mode) => {
            const cfg = VIEW_META[mode];
            const Icon = cfg.icon;
            const active = viewMode === mode;
            return (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-mono transition-colors ${
                  active
                    ? "bg-purple-500/20 text-purple-300 border border-purple-500/40"
                    : "text-gray-400 hover:text-white hover:bg-white/[0.04] border border-transparent"
                }`}
              >
                <Icon size={11} />
                {cfg.label}
              </button>
            );
          })}
        </div>

        {/* 右：今日摘要 + 添加按钮 */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* 今日摘要徽章 */}
          <div
            className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-md border border-purple-500/20 bg-purple-500/[0.06]"
            title="今日日程数"
          >
            <Clock size={11} className="text-purple-400" />
            <span className="text-[11px] font-mono text-purple-300">
              今日 {todaySchedules.length}
            </span>
            {/* 即将开始徽章 */}
            {upcomingSchedules.length > 0 && (
              <span className="flex items-center gap-0.5 ml-1 px-1 py-0.5 rounded bg-amber-500/20 border border-amber-500/40">
                <Bell size={9} className="text-amber-400 animate-pulse" />
                <span className="text-[9px] font-mono text-amber-300">
                  {upcomingSchedules.length} 即将开始
                </span>
              </span>
            )}
          </div>
          {/* 添加按钮 */}
          <button
            onClick={openAddForm}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-purple-500/40 bg-purple-500/15 hover:bg-purple-500/25 transition-colors"
            title="添加新日程"
          >
            <Plus size={13} className="text-purple-300" />
            <span className="text-[11px] font-mono text-purple-200">添加</span>
          </button>
        </div>
      </div>

      {/* ============ 主内容区：左日历 + 右详情 ============ */}
      <div className="flex-1 min-h-0 grid grid-cols-12 gap-3 p-3 overflow-hidden">
        {/* ============ 左侧：日历视图 ============ */}
        <div className="col-span-8 h-full flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
          {/* 日历导航条 */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06]">
            <div className="flex items-center gap-1.5">
              <button
                onClick={handlePrev}
                className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.06] transition-colors"
                title="上一个"
              >
                <ChevronLeft size={15} />
              </button>
              <button
                onClick={handleToday}
                className="px-2 py-1 rounded-md text-[11px] font-mono text-gray-300 hover:text-white hover:bg-white/[0.06] border border-white/[0.06] transition-colors"
              >
                今天
              </button>
              <button
                onClick={handleNext}
                className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.06] transition-colors"
                title="下一个"
              >
                <ChevronRight size={15} />
              </button>
            </div>
            <div className="text-[12px] font-mono text-purple-300 truncate">
              {cursorLabel}
            </div>
            <div className="text-[10px] font-mono text-gray-600">
              {visibleSchedules.length} 项
            </div>
          </div>

          {/* 日历内容区（按视图模式切换） */}
          <div className="flex-1 min-h-0 overflow-hidden">
            {schedules.length === 0 ? (
              /* 无数据占位：禁止假数据 */
              <div className="h-full flex flex-col items-center justify-center gap-2 text-center px-6">
                <CalendarIcon size={32} className="text-gray-700" />
                <div className="text-[13px] text-gray-500">暂无日程</div>
                <div className="text-[11px] text-gray-600">点击右上角"添加"创建第一个日程</div>
                <button
                  onClick={openAddForm}
                  className="mt-2 flex items-center gap-1 px-3 py-1.5 rounded-md border border-purple-500/40 bg-purple-500/15 hover:bg-purple-500/25 transition-colors"
                >
                  <Plus size={12} className="text-purple-300" />
                  <span className="text-[11px] font-mono text-purple-200">点击添加</span>
                </button>
              </div>
            ) : viewMode === "day" ? (
              /* 日视图：按小时排列的日程时间线 */
              <DayView
                date={cursorDate}
                schedules={visibleSchedules}
                selectedId={selectedId}
                onSelect={setSelectedId}
                upcomingIds={new Set(upcomingSchedules.map((s) => s.id))}
              />
            ) : viewMode === "week" ? (
              /* 周视图：7 列日程块 */
              <WeekView
                date={cursorDate}
                schedules={visibleSchedules}
                selectedId={selectedId}
                onSelect={setSelectedId}
                upcomingIds={new Set(upcomingSchedules.map((s) => s.id))}
              />
            ) : (
              /* 月视图：6×7 网格 */
              <MonthView
                date={cursorDate}
                schedules={visibleSchedules}
                selectedId={selectedId}
                onSelect={setSelectedId}
              />
            )}
          </div>
        </div>

        {/* ============ 右侧：选中日程详情 ============ */}
        <div className="col-span-4 h-full flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
          {selectedItem ? (
            <ScheduleDetail
              item={selectedItem}
              generatingNotes={generatingNotes}
              onGenerateNotes={handleGenerateNotes}
              onAddAction={handleAddActionItem}
              onToggleAction={handleToggleAction}
              onDeleteAction={handleDeleteAction}
              onDeleteSchedule={handleDeleteSchedule}
              onEdit={openEditForm}
              onSuggestBestTime={(attendees) => {
                setSuggestAttendees(attendees.join("、"));
                handleSuggestBestTime(attendees);
              }}
              newActionContent={newActionContent}
              newActionAssignee={newActionAssignee}
              setNewActionContent={setNewActionContent}
              setNewActionAssignee={setNewActionAssignee}
            />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-2 text-center px-4">
              <Sparkles size={28} className="text-gray-700" />
              <div className="text-[12px] text-gray-500">选中左侧日程查看详情</div>
              <div className="text-[10px] text-gray-600 font-mono">
                含会议纪要 / 行动项 / 冲突提示
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ============ 添加 / 编辑日程 Modal ============ */}
      <AnimatePresence>
        {formOpen && (
          <ScheduleFormModal
            editing={editingItem}
            onClose={() => {
              setFormOpen(false);
              setEditingItem(null);
            }}
            onSave={(item) => {
              // 新建：补 id / createdAt；编辑：保留原 id / createdAt
              const finalItem: ScheduleItem = editingItem
                ? {
                    ...editingItem,
                    ...item,
                    id: editingItem.id,
                    createdAt: editingItem.createdAt,
                  }
                : {
                    ...item,
                    id: genId(),
                    createdAt: new Date().toISOString(),
                  };
              handleSaveSchedule(finalItem);
            }}
          />
        )}
      </AnimatePresence>

      {/* ============ 冲突确认 Modal ============ */}
      <AnimatePresence>
        {conflictWarning && (
          <ConflictConfirmModal
            pending={conflictWarning.pending}
            conflicts={conflictWarning.conflicts}
            onCancel={() => setConflictWarning(null)}
            onConfirm={handleConfirmConflictSave}
          />
        )}
      </AnimatePresence>

      {/* ============ 最佳时间建议 Modal ============ */}
      <AnimatePresence>
        {suggestions && (
          <SuggestionsModal
            suggestions={suggestions}
            attendees={suggestAttendees}
            onClose={() => {
              setSuggestions(null);
              setSuggestAttendees("");
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// 子组件：日视图
// ============================================

function DayView({
  date,
  schedules,
  selectedId,
  onSelect,
  upcomingIds,
}: {
  date: Date;
  schedules: ScheduleItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  upcomingIds: Set<string>;
}) {
  // 按小时分块（0-23），将日程归入对应小时
  const hourBlocks = useMemo(() => {
    const blocks: { hour: number; items: ScheduleItem[] }[] = [];
    for (let h = 0; h < 24; h++) {
      const items = schedules.filter(
        (s) => new Date(s.startTime).getHours() === h
      );
      blocks.push({ hour: h, items });
    }
    return blocks;
  }, [schedules]);

  return (
    <div className="h-full overflow-y-auto p-2">
      <div className="space-y-0.5">
        {hourBlocks.map(({ hour, items }) => (
          <div key={hour} className="flex gap-2 min-h-[28px]">
            {/* 小时标签 */}
            <div className="w-10 flex-shrink-0 text-right pt-0.5">
              <span className="text-[10px] font-mono text-gray-600">
                {String(hour).padStart(2, "0")}:00
              </span>
            </div>
            {/* 日程块 */}
            <div className="flex-1 min-w-0 border-l border-white/[0.04] pl-2 space-y-1">
              {items.length === 0 ? (
                <div className="h-[20px]" />
              ) : (
                items.map((item) => {
                  const cfg = TYPE_META[item.type];
                  const Icon = cfg.icon;
                  const active = selectedId === item.id;
                  const upcoming = upcomingIds.has(item.id);
                  return (
                    <motion.button
                      key={item.id}
                      initial={{ opacity: 0, x: -4 }}
                      animate={{ opacity: 1, x: 0 }}
                      onClick={() => onSelect(item.id)}
                      className={`w-full text-left rounded-md px-2 py-1.5 border transition-colors ${
                        active
                          ? "border-purple-500/50 bg-purple-500/10"
                          : upcoming
                            ? "border-amber-500/40 bg-amber-500/[0.06]"
                            : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"
                      }`}
                      style={{ borderLeftColor: cfg.color, borderLeftWidth: 2 }}
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Icon size={11} style={{ color: cfg.color }} className="flex-shrink-0" />
                        <span className="text-[11px] text-gray-200 truncate flex-1">
                          {item.title}
                        </span>
                        {upcoming && (
                          <Bell size={10} className="text-amber-400 animate-pulse flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-[10px] font-mono text-gray-500">
                        <span>
                          {formatTime(item.startTime)} - {formatTime(item.endTime)}
                        </span>
                        {item.location && (
                          <span className="truncate flex items-center gap-0.5">
                            <MapPin size={9} />
                            {item.location}
                          </span>
                        )}
                      </div>
                    </motion.button>
                  );
                })
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// 子组件：周视图
// ============================================

function WeekView({
  date,
  schedules,
  selectedId,
  onSelect,
  upcomingIds,
}: {
  date: Date;
  schedules: ScheduleItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  upcomingIds: Set<string>;
}) {
  const days = useMemo(() => getWeekDays(date), [date]);
  const today = new Date();

  return (
    <div className="h-full flex flex-col p-2">
      {/* 周几表头 */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {days.map((d, i) => {
          const isToday = isSameDay(d, today);
          return (
            <div
              key={i}
              className={`text-center py-1 rounded-md border ${
                isToday
                  ? "border-purple-500/40 bg-purple-500/10"
                  : "border-white/[0.06] bg-white/[0.02]"
              }`}
            >
              <div className="text-[10px] font-mono text-gray-500">周{WEEK_LABELS[i]}</div>
              <div
                className={`text-[13px] font-mono ${
                  isToday ? "text-purple-300" : "text-gray-300"
                }`}
              >
                {d.getDate()}
              </div>
            </div>
          );
        })}
      </div>
      {/* 7 列日程块 */}
      <div className="flex-1 min-h-0 grid grid-cols-7 gap-1 overflow-y-auto">
        {days.map((d, i) => {
          const dayItems = schedules.filter((s) => isScheduleOnDate(s, d));
          return (
            <div
              key={i}
              className="min-h-0 flex flex-col gap-1 rounded-md border border-white/[0.04] bg-white/[0.01] p-1 overflow-y-auto"
            >
              {dayItems.length === 0 ? (
                <div className="flex-1 flex items-center justify-center">
                  <span className="text-[9px] font-mono text-gray-700">—</span>
                </div>
              ) : (
                dayItems.map((item) => {
                  const cfg = TYPE_META[item.type];
                  const Icon = cfg.icon;
                  const active = selectedId === item.id;
                  const upcoming = upcomingIds.has(item.id);
                  return (
                    <motion.button
                      key={item.id}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      onClick={() => onSelect(item.id)}
                      className={`text-left rounded-md px-1.5 py-1 border transition-colors ${
                        active
                          ? "border-purple-500/50 bg-purple-500/15"
                          : upcoming
                            ? "border-amber-500/40 bg-amber-500/[0.08]"
                            : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05]"
                      }`}
                      style={{ borderLeftColor: cfg.color, borderLeftWidth: 2 }}
                    >
                      <div className="flex items-center gap-0.5 min-w-0">
                        <Icon size={9} style={{ color: cfg.color }} className="flex-shrink-0" />
                        <span className="text-[10px] text-gray-200 truncate flex-1">
                          {item.title}
                        </span>
                        {upcoming && (
                          <Bell size={8} className="text-amber-400 animate-pulse flex-shrink-0" />
                        )}
                      </div>
                      <div className="text-[9px] font-mono text-gray-500 mt-0.5">
                        {formatTime(item.startTime)}
                      </div>
                    </motion.button>
                  );
                })
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// 子组件：月视图
// ============================================

function MonthView({
  date,
  schedules,
  selectedId,
  onSelect,
}: {
  date: Date;
  schedules: ScheduleItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const grid = useMemo(() => getMonthGrid(date), [date]);
  const today = new Date();
  const currentMonth = date.getMonth();

  return (
    <div className="h-full flex flex-col p-2">
      {/* 周几表头 */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {WEEK_LABELS.map((w) => (
          <div
            key={w}
            className="text-center py-1 text-[10px] font-mono text-gray-500 border border-white/[0.04] rounded"
          >
            周{w}
          </div>
        ))}
      </div>
      {/* 6×7 日期网格 */}
      <div className="flex-1 min-h-0 grid grid-rows-6 gap-1">
        {Array.from({ length: 6 }).map((_, rowIdx) => (
          <div key={rowIdx} className="grid grid-cols-7 gap-1">
            {grid.slice(rowIdx * 7, rowIdx * 7 + 7).map((d, colIdx) => {
              const inMonth = d.getMonth() === currentMonth;
              const isToday = isSameDay(d, today);
              const dayItems = schedules.filter((s) => isScheduleOnDate(s, d));
              return (
                <div
                  key={colIdx}
                  className={`min-h-0 rounded-md border p-1 overflow-hidden flex flex-col ${
                    isToday
                      ? "border-purple-500/40 bg-purple-500/[0.08]"
                      : inMonth
                        ? "border-white/[0.06] bg-white/[0.02]"
                        : "border-white/[0.03] bg-white/[0.005] opacity-50"
                  }`}
                >
                  <div
                    className={`text-[10px] font-mono mb-0.5 ${
                      isToday ? "text-purple-300" : inMonth ? "text-gray-400" : "text-gray-700"
                    }`}
                  >
                    {d.getDate()}
                  </div>
                  <div className="flex-1 min-h-0 overflow-y-auto space-y-0.5">
                    {dayItems.slice(0, 3).map((item) => {
                      const cfg = TYPE_META[item.type];
                      const active = selectedId === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => onSelect(item.id)}
                          className={`w-full text-left rounded px-1 py-0.5 border text-[9px] truncate transition-colors ${
                            active
                              ? "border-purple-500/50 bg-purple-500/15"
                              : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05]"
                          }`}
                          style={{ borderLeftColor: cfg.color, borderLeftWidth: 2 }}
                          title={`${item.title} ${formatTime(item.startTime)}`}
                        >
                          <span className="text-gray-300">{item.title}</span>
                        </button>
                      );
                    })}
                    {dayItems.length > 3 && (
                      <div className="text-[9px] font-mono text-gray-600 text-center">
                        +{dayItems.length - 3}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// 子组件：日程详情面板
// ============================================

function ScheduleDetail({
  item,
  generatingNotes,
  onGenerateNotes,
  onAddAction,
  onToggleAction,
  onDeleteAction,
  onDeleteSchedule,
  onEdit,
  onSuggestBestTime,
  newActionContent,
  newActionAssignee,
  setNewActionContent,
  setNewActionAssignee,
}: {
  item: ScheduleItem;
  generatingNotes: string | null;
  onGenerateNotes: (item: ScheduleItem) => void;
  onAddAction: (scheduleId: string, content: string, assignee: string, dueDate?: string) => void;
  onToggleAction: (scheduleId: string, actionId: string) => void;
  onDeleteAction: (scheduleId: string, actionId: string) => void;
  onDeleteSchedule: (id: string) => void;
  onEdit: (item: ScheduleItem) => void;
  onSuggestBestTime: (attendees: string[]) => void;
  newActionContent: string;
  newActionAssignee: string;
  setNewActionContent: (v: string) => void;
  setNewActionAssignee: (v: string) => void;
}) {
  const cfg = TYPE_META[item.type];
  const Icon = cfg.icon;
  const status = STATUS_META[item.status];
  const actionItems = item.actionItems || [];
  const completedCount = actionItems.filter((a) => a.completed).length;
  const isGenerating = generatingNotes === item.id;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* 头部：标题 + 类型 + 状态 */}
      <div className="flex items-start justify-between p-3 border-b border-white/[0.06]">
        <div className="flex items-start gap-2 min-w-0">
          <div
            className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 border"
            style={{ background: cfg.bg, borderColor: cfg.border }}
          >
            <Icon size={14} style={{ color: cfg.color }} />
          </div>
          <div className="min-w-0">
            <h3 className="text-[14px] font-semibold text-white leading-tight">
              {item.title}
            </h3>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <span
                className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}
              >
                {cfg.label}
              </span>
              <span
                className="text-[10px] font-mono px-1.5 py-0.5 rounded border"
                style={{ color: status.color, borderColor: `${status.color}40` }}
              >
                {status.label}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => onEdit(item)}
            className="p-1 rounded-md text-gray-400 hover:text-purple-300 hover:bg-purple-500/10 transition-colors"
            title="编辑"
          >
            <Pencil size={13} />
          </button>
          <button
            onClick={() => {
              if (window.confirm("确定删除此日程？")) onDeleteSchedule(item.id);
            }}
            className="p-1 rounded-md text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            title="删除"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {/* 内容区：可滚动 */}
      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
        {/* 基本信息块 */}
        <div className="space-y-1.5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
          <div className="flex items-center gap-2 text-[11px]">
            <Clock size={12} className="text-purple-400 flex-shrink-0" />
            <span className="font-mono text-gray-400">时间</span>
            <span className="text-gray-200 truncate">
              {formatDateTime(item.startTime)} → {formatTime(item.endTime)}
            </span>
          </div>
          {item.location && (
            <div className="flex items-center gap-2 text-[11px]">
              <MapPin size={12} className="text-purple-400 flex-shrink-0" />
              <span className="font-mono text-gray-400">地点</span>
              <span className="text-gray-200 truncate">{item.location}</span>
            </div>
          )}
          {item.attendees && item.attendees.length > 0 && (
            <div className="flex items-start gap-2 text-[11px]">
              <Users size={12} className="text-purple-400 flex-shrink-0 mt-0.5" />
              <span className="font-mono text-gray-400 flex-shrink-0">参会</span>
              <div className="flex flex-wrap gap-1">
                {item.attendees.map((a, i) => (
                  <span
                    key={i}
                    className="px-1.5 py-0.5 rounded bg-purple-500/10 border border-purple-500/20 text-[10px] font-mono text-purple-300"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}
          {item.description && (
            <div className="flex items-start gap-2 text-[11px] pt-1 border-t border-white/[0.04]">
              <FileText size={12} className="text-purple-400 flex-shrink-0 mt-0.5" />
              <span className="font-mono text-gray-400 flex-shrink-0">描述</span>
              <span className="text-gray-300 leading-relaxed">{item.description}</span>
            </div>
          )}
          {/* 最佳会议时间建议按钮（基于参会人空闲时段分析） */}
          <button
            onClick={() => onSuggestBestTime(item.attendees || [])}
            className="w-full flex items-center justify-center gap-1.5 mt-2 px-2 py-1.5 rounded-md border border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20 transition-colors"
            title="分析参会人未来 7 天空闲时段"
          >
            <Lightbulb size={12} className="text-purple-300" />
            <span className="text-[11px] font-mono text-purple-200">
              建议最佳会议时间
            </span>
          </button>
        </div>

        {/* 会议纪要块 */}
        <div className="rounded-lg border border-purple-500/20 bg-purple-500/[0.04] p-2.5">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1.5">
              <FileText size={12} className="text-purple-400" />
              <span className="text-[11px] font-medium text-white">会议纪要</span>
            </div>
            <button
              onClick={() => onGenerateNotes(item)}
              disabled={isGenerating}
              className="flex items-center gap-1 px-2 py-1 rounded-md border border-purple-500/40 bg-purple-500/15 hover:bg-purple-500/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="调用 Hermes Runtime 生成会议纪要"
            >
              {isGenerating ? (
                <Loader2 size={11} className="animate-spin text-purple-300" />
              ) : (
                <Sparkles size={11} className="text-purple-300" />
              )}
              <span className="text-[10px] font-mono text-purple-200">
                {isGenerating ? "生成中..." : "生成纪要"}
              </span>
            </button>
          </div>
          {item.meetingNotes ? (
            <div className="text-[11px] text-gray-300 leading-relaxed whitespace-pre-wrap font-mono bg-white/[0.02] rounded p-2 border border-white/[0.04] max-h-[180px] overflow-y-auto">
              {item.meetingNotes}
            </div>
          ) : (
            <div className="text-[11px] text-gray-600 italic">
              暂无纪要，点击"生成纪要"由 Hermes 自动生成
            </div>
          )}
        </div>

        {/* 行动项块 */}
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 size={12} className="text-purple-400" />
              <span className="text-[11px] font-medium text-white">行动项</span>
              <span className="text-[10px] font-mono text-gray-500">
                {completedCount}/{actionItems.length}
              </span>
            </div>
          </div>
          {/* 已有行动项列表 */}
          {actionItems.length > 0 ? (
            <div className="space-y-1 mb-2">
              {actionItems.map((a) => (
                <div
                  key={a.id}
                  className="flex items-start gap-1.5 rounded border border-white/[0.04] bg-white/[0.01] px-2 py-1.5"
                >
                  <button
                    onClick={() => onToggleAction(item.id, a.id)}
                    className={`mt-0.5 flex-shrink-0 ${
                      a.completed ? "text-green-400" : "text-gray-600 hover:text-purple-300"
                    }`}
                    title={a.completed ? "标记为未完成" : "标记为已完成"}
                  >
                    <CheckCircle2 size={13} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div
                      className={`text-[11px] leading-snug ${
                        a.completed ? "text-gray-500 line-through" : "text-gray-200"
                      }`}
                    >
                      {a.content}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-[10px] font-mono text-gray-600">
                      <span className="flex items-center gap-0.5">
                        <Users size={9} />
                        {a.assignee}
                      </span>
                      {a.dueDate && (
                        <span className="flex items-center gap-0.5">
                          <Clock size={9} />
                          {a.dueDate}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => onDeleteAction(item.id, a.id)}
                    className="flex-shrink-0 text-gray-600 hover:text-red-400 transition-colors"
                    title="删除行动项"
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[11px] text-gray-600 italic mb-2">暂无行动项</div>
          )}
          {/* 新增行动项输入 */}
          <div className="space-y-1.5">
            <input
              type="text"
              value={newActionContent}
              onChange={(e) => setNewActionContent(e.target.value)}
              placeholder="行动项内容..."
              className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-purple-500/40"
            />
            <div className="flex gap-1.5">
              <input
                type="text"
                value={newActionAssignee}
                onChange={(e) => setNewActionAssignee(e.target.value)}
                placeholder="负责人"
                className="flex-1 px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-purple-500/40"
              />
              <button
                onClick={() => {
                  if (newActionContent.trim()) {
                    onAddAction(item.id, newActionContent, newActionAssignee);
                    setNewActionContent("");
                    setNewActionAssignee("");
                  }
                }}
                className="flex items-center gap-1 px-2 py-1.5 rounded-md border border-purple-500/40 bg-purple-500/15 hover:bg-purple-500/25 transition-colors"
              >
                <Plus size={11} className="text-purple-300" />
                <span className="text-[10px] font-mono text-purple-200">添加</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 子组件：添加 / 编辑表单 Modal
// ============================================

function ScheduleFormModal({
  editing,
  onClose,
  onSave,
}: {
  editing: ScheduleItem | null;
  onClose: () => void;
  onSave: (item: ScheduleItem) => void;
}) {
  // 表单字段（受控）
  const [type, setType] = useState<ScheduleType>(editing?.type || "meeting");
  const [title, setTitle] = useState(editing?.title || "");
  const [description, setDescription] = useState(editing?.description || "");
  const [startTime, setStartTime] = useState(
    editing ? isoToLocalInput(editing.startTime) : ""
  );
  const [endTime, setEndTime] = useState(
    editing ? isoToLocalInput(editing.endTime) : ""
  );
  const [location, setLocation] = useState(editing?.location || "");
  const [attendees, setAttendees] = useState(
    editing?.attendees ? editing.attendees.join("、") : ""
  );
  const [status, setStatus] = useState<ScheduleStatus>(
    editing?.status || "scheduled"
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !startTime || !endTime) {
      window.alert("请填写标题、开始时间和结束时间");
      return;
    }
    const startISO = localInputToISO(startTime);
    const endISO = localInputToISO(endTime);
    if (new Date(endISO).getTime() <= new Date(startISO).getTime()) {
      window.alert("结束时间必须晚于开始时间");
      return;
    }
    onSave({
      id: editing?.id || `schedule_${Date.now()}`,
      createdAt: editing?.createdAt || new Date().toISOString(),
      type,
      title: title.trim(),
      description: description.trim() || undefined,
      startTime: startISO,
      endTime: endISO,
      status,
      location: location.trim() || undefined,
      attendees: attendees
        ? attendees.split(/[、,，\s]+/).filter(Boolean)
        : undefined,
      meetingNotes: editing?.meetingNotes,
      actionItems: editing?.actionItems,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 10 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 10 }}
        transition={{ duration: 0.2 }}
        className="relative w-full max-w-lg max-h-[90vh] flex flex-col rounded-xl border border-purple-500/20 bg-[#0a0e1a] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <CalendarIcon size={14} className="text-purple-400" />
            <h3 className="text-[14px] font-semibold text-white">
              {editing ? "编辑日程" : "添加日程"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.06] transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Modal Body：表单 */}
        <form onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
          {/* 类型选择 */}
          <div>
            <label className="block text-[11px] font-mono text-gray-500 mb-1">类型</label>
            <div className="grid grid-cols-4 gap-1.5">
              {(Object.keys(TYPE_META) as ScheduleType[]).map((t) => {
                const cfg = TYPE_META[t];
                const Icon = cfg.icon;
                const active = type === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={`flex items-center justify-center gap-1 py-1.5 rounded-md border text-[11px] font-mono transition-colors ${
                      active ? "border-purple-500/50 bg-purple-500/15 text-purple-200" : "border-white/[0.06] bg-white/[0.02] text-gray-400 hover:text-white"
                    }`}
                    style={active ? { borderColor: cfg.border, background: cfg.bg, color: cfg.color } : undefined}
                  >
                    <Icon size={11} />
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 标题 */}
          <div>
            <label className="block text-[11px] font-mono text-gray-500 mb-1">
              标题 <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="例如：产品周会"
              className="w-full px-2.5 py-2 rounded-md bg-white/[0.03] border border-white/[0.06] text-[12px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-purple-500/40"
              required
            />
          </div>

          {/* 时间 */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] font-mono text-gray-500 mb-1">
                开始时间 <span className="text-red-400">*</span>
              </label>
              <input
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-2.5 py-2 rounded-md bg-white/[0.03] border border-white/[0.06] text-[12px] text-gray-200 focus:outline-none focus:border-purple-500/40"
                required
              />
            </div>
            <div>
              <label className="block text-[11px] font-mono text-gray-500 mb-1">
                结束时间 <span className="text-red-400">*</span>
              </label>
              <input
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-2.5 py-2 rounded-md bg-white/[0.03] border border-white/[0.06] text-[12px] text-gray-200 focus:outline-none focus:border-purple-500/40"
                required
              />
            </div>
          </div>

          {/* 参会人 */}
          <div>
            <label className="block text-[11px] font-mono text-gray-500 mb-1">
              参会人（用顿号/逗号分隔）
            </label>
            <input
              type="text"
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              placeholder="例如：张三、李四、王五"
              className="w-full px-2.5 py-2 rounded-md bg-white/[0.03] border border-white/[0.06] text-[12px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-purple-500/40"
            />
          </div>

          {/* 地点 */}
          <div>
            <label className="block text-[11px] font-mono text-gray-500 mb-1">地点</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="例如：会议室 A / 线上 Zoom"
              className="w-full px-2.5 py-2 rounded-md bg-white/[0.03] border border-white/[0.06] text-[12px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-purple-500/40"
            />
          </div>

          {/* 描述 */}
          <div>
            <label className="block text-[11px] font-mono text-gray-500 mb-1">描述</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="可选：议程、备注..."
              rows={2}
              className="w-full px-2.5 py-2 rounded-md bg-white/[0.03] border border-white/[0.06] text-[12px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-purple-500/40 resize-none"
            />
          </div>

          {/* 状态（仅编辑时可改） */}
          {editing && (
            <div>
              <label className="block text-[11px] font-mono text-gray-500 mb-1">状态</label>
              <div className="flex flex-wrap gap-1.5">
                {(Object.keys(STATUS_META) as ScheduleStatus[]).map((s) => {
                  const cfg = STATUS_META[s];
                  const active = status === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setStatus(s)}
                      className={`px-2 py-1 rounded-md border text-[11px] font-mono transition-colors ${
                        active ? "bg-white/[0.08]" : "border-white/[0.06] bg-white/[0.02] text-gray-400"
                      }`}
                      style={active ? { color: cfg.color, borderColor: `${cfg.color}40` } : undefined}
                    >
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </form>

        {/* Modal Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06] bg-white/[0.01]">
          <span className="text-[10px] font-mono text-gray-600">
            数据持久化：localStorage
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-md border border-white/[0.06] text-[11px] font-mono text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors"
            >
              取消
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md border border-purple-500/40 bg-purple-500/20 hover:bg-purple-500/30 transition-colors"
            >
              <Send size={11} className="text-purple-300" />
              <span className="text-[11px] font-mono text-purple-200">
                {editing ? "保存" : "创建"}
              </span>
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 子组件：冲突确认 Modal
// ============================================

function ConflictConfirmModal({
  pending,
  conflicts,
  onCancel,
  onConfirm,
}: {
  pending: ScheduleItem;
  conflicts: ScheduleItem[];
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.95, y: 10 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 10 }}
        transition={{ duration: 0.2 }}
        className="relative w-full max-w-md rounded-xl border border-amber-500/30 bg-[#0a0e1a] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06]">
          <AlertTriangle size={15} className="text-amber-400" />
          <h3 className="text-[14px] font-semibold text-white">检测到时间冲突</h3>
        </div>
        {/* Body */}
        <div className="p-4 space-y-2">
          <div className="text-[12px] text-gray-300">
            日程「{pending.title}」与以下 {conflicts.length} 项日程时间重叠：
          </div>
          <div className="space-y-1 max-h-[200px] overflow-y-auto">
            {conflicts.map((c) => {
              const cfg = TYPE_META[c.type];
              return (
                <div
                  key={c.id}
                  className="flex items-center gap-2 rounded border border-amber-500/20 bg-amber-500/[0.06] px-2 py-1.5"
                >
                  <span
                    className="text-[10px] font-mono px-1 py-0.5 rounded"
                    style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}
                  >
                    {cfg.label}
                  </span>
                  <span className="text-[11px] text-gray-200 truncate flex-1">{c.title}</span>
                  <span className="text-[10px] font-mono text-gray-500">
                    {formatTime(c.startTime)} - {formatTime(c.endTime)}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="text-[11px] text-amber-400/80 italic">
            提示：可仍保存此日程，系统将标记为冲突。
          </div>
        </div>
        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-white/[0.06] bg-white/[0.01]">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 rounded-md border border-white/[0.06] text-[11px] font-mono text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors"
          >
            返回修改
          </button>
          <button
            onClick={onConfirm}
            className="px-3 py-1.5 rounded-md border border-amber-500/40 bg-amber-500/20 hover:bg-amber-500/30 transition-colors text-[11px] font-mono text-amber-200"
          >
            仍要保存
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 子组件：最佳时间建议 Modal
// ============================================

function SuggestionsModal({
  suggestions,
  attendees,
  onClose,
}: {
  suggestions: string[];
  attendees: string;
  onClose: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 10 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 10 }}
        transition={{ duration: 0.2 }}
        className="relative w-full max-w-md rounded-xl border border-purple-500/30 bg-[#0a0e1a] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06]">
          <Lightbulb size={15} className="text-purple-400" />
          <h3 className="text-[14px] font-semibold text-white">最佳会议时间建议</h3>
        </div>
        {/* Body */}
        <div className="p-4 space-y-2">
          {attendees && (
            <div className="text-[11px] text-gray-400">
              参会人：<span className="text-purple-300 font-mono">{attendees}</span>
            </div>
          )}
          <div className="text-[12px] text-gray-300 mb-2">
            基于参会人未来 7 天日程，推荐以下空闲时段：
          </div>
          <div className="space-y-1">
            {suggestions.map((s, i) => (
              <div
                key={i}
                className="flex items-center gap-2 rounded border border-purple-500/20 bg-purple-500/[0.06] px-2 py-1.5"
              >
                <span className="text-[10px] font-mono text-purple-400 w-5">
                  {i + 1}.
                </span>
                <span className="text-[11px] font-mono text-gray-200 flex-1">{s}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-white/[0.06] bg-white/[0.01]">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-purple-500/40 bg-purple-500/20 hover:bg-purple-500/30 transition-colors text-[11px] font-mono text-purple-200"
          >
            知道了
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
